import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import {
  history,
  setLocale,
  useAntdConfigSetter,
  useLocation,
  useModel,
} from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTenant,
  useTheme,
} from '@zhiyue/core';
import { AiChat } from '@zhiyue/xiaoyueyue';
import '@zhiyue/core/style.css';

import { Spin } from 'antd';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import { sessionAdapter } from '@/adapters/sessionAdapter';
import { AvatarDropdown, NotificationIcon, ThemeToggle } from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import {
  getNavigationForPath,
  getSettingsPath,
  getSplitMenuSelectedKeys,
} from '@/navigation/centerNavigation';
import { filterShellMenu } from '@/navigation/shellMenu';
import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import { DevPermissionDrawerFooter } from '@/permissions/DevPermissionBypassSetting';
import { EmployeePortalProvider } from '@/permissions/EmployeePortalProvider';
import {
  ATTENDANCE_ROUTES,
  type EmployeePortalEffectiveFeatures,
  getEffectiveEmployeePortalAccess,
  getFirstAttendancePath,
  getFirstPayrollPath,
  getFirstPerformancePath,
  getFirstSocialInsurancePath,
  PAYROLL_ROUTES,
  PERFORMANCE_ROUTES,
  PERSONAL_ROUTES,
  SOCIAL_INSURANCE_ROUTES,
} from '@/permissions/employeePortalFeatures';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import ShellThemeBoundary from '@/styles/ShellThemeBoundary';
import { ensureInitialNavTheme, getShellTheme } from '@/styles/shellTheme';
import defaultSettings from '../config/defaultSettings';
import getAccess from './access';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import '@/styles/shell.less';

export const request: RequestConfig = {
  ...errorConfig,
};

const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  navTheme: ensureInitialNavTheme(),
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [
    user.id,
    user.memberUuid,
    user.uuid,
    user.username,
    user.tenantUuid,
    user.lastLoginOrganizationUuid,
  ]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  ensureInitialNavTheme();
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [
          {
            key: 'help',
            label: '帮助',
            href: 'https://docs.shanhuyun.cn/',
            target: '_blank',
          },
          ...[
            { key: 'zh-CN', label: '简体中文' },
            { key: 'en-US', label: 'English' },
            { key: 'ja-JP', label: '日本語' },
            { key: 'zh-TW', label: '繁體中文' },
          ].map((item) => ({ ...item, key: `locale:${item.key}` })),
        ],
        onAction: (key) => {
          if (key.startsWith('locale:')) setLocale(key.slice(7));
        },
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.href = applicationReturnTo;
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
}> = ({ logo, appTitle }) => {
  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
    </div>
  );
};

const CenterNavigation: React.FC<{
  visible: Record<string, boolean>;
  settingsPath?: string;
}> = ({ visible, settingsPath }) => {
  const { pathname } = useLocation();
  const activeCenter = getNavigationForPath(pathname);
  const items = [
    { key: 'employee', label: '员工中心', root: '/me' },
    { key: 'attendance', label: '考勤中心', root: '/attendance-center' },
    {
      key: 'social-insurance',
      label: '社保中心',
      root: '/social-insurance-center',
    },
    { key: 'performance', label: '绩效中心', root: '/performance-center' },
    { key: 'payroll', label: '薪酬中心', root: '/payroll-center' },
  ].filter((item) => visible[item.key]);
  if (settingsPath)
    items.push({ key: 'settings', label: '设置', root: settingsPath });
  return (
    <nav className="app-center-nav" aria-label="业务中心">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`app-center-nav-item${
            activeCenter === item.key ? ' is-active' : ''
          }`}
          aria-current={activeCenter === item.key ? 'page' : undefined}
          onClick={() => history.push(item.root)}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { currentTenantUuid, tenants } = useTenant();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
    loading: true,
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey &&
      lastSynced.loading === loading
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey &&
        prev.loading === loading
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        loading,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
      loading,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    setInitialState((prev: any) =>
      prev
        ? {
            ...prev,
            currentTenantIsAdmin: tenants.some(
              (tenant) =>
                tenant.id === currentTenantUuid &&
                tenant.roleLabel === '管理员',
            ),
          }
        : prev,
    );
  }, [tenants, currentTenantUuid, setInitialState]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useLayoutEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    document.documentElement.dataset.navTheme = navTheme;

    setAntdConfig((prevConfig) => ({
      ...prevConfig,
      theme: getShellTheme(navTheme),
    }));
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export const rootContainer = (container: React.ReactNode) => {
  return <RootSessionContainer>{container}</RootSessionContainer>;
};

const RootSessionContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return <SessionProvider config={sessionConfig}>{children}</SessionProvider>;
};

const RuntimeShellSync: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { initialState, setInitialState } = useModel('@@initialState') as {
    initialState?: {
      loading?: boolean;
      permissionRetry?: number;
      settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
    };
    setInitialState: (updater: any) => void;
  };

  return (
    <>
      <SessionSync setInitialState={setInitialState} />
      <EmployeePortalProvider
        permissionRetry={initialState?.permissionRetry}
        setInitialState={setInitialState}
      >
        {process.env.NODE_ENV === 'development' && (
          <SettingDrawer
            disableUrlParams
            drawerProps={{
              footer: (
                <DevPermissionDrawerFooter
                  onRefreshPermissions={() =>
                    setInitialState((prev: any) => ({
                      ...prev,
                      permissionRetry: (prev?.permissionRetry || 0) + 1,
                    }))
                  }
                />
              ),
            }}
            settings={initialState?.settings}
            onSettingChange={(settings) =>
              setInitialState((prev: any) => ({ ...prev, settings }))
            }
          />
        )}
        {initialState?.loading ? <Spin fullscreen /> : children}
      </EmployeePortalProvider>
      <FloatingAiChat />
    </>
  );
};

export const innerProvider = (container: React.ReactNode) => (
  <ShellThemeBoundary>
    <RuntimeShellSync>{container}</RuntimeShellSync>
  </ShellThemeBoundary>
);

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();

  if (
    loading ||
    !currentUser ||
    publicPaths.includes(history.location.pathname)
  ) {
    return null;
  }

  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = readCurrentTenantUuid() || currentUser.tenantUuid;
        const organizationUuid =
          (
            currentUser as CurrentUser & {
              organizationUuid?: string;
              lastLoginOrganizationUuid?: string;
            }
          ).organizationUuid ||
          (
            currentUser as CurrentUser & {
              lastLoginOrganizationUuid?: string;
            }
          ).lastLoginOrganizationUuid;

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  employeePortalFeatures?: EmployeePortalEffectiveFeatures;
  loading?: boolean;
}> {
  return {
    loading: true,
    authorizedResourceAccess: {},
    currentTenantIsAdmin: false,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const access = getAccess(initialState);
  const settingsPath = getSettingsPath(access);
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    className: 'hr-shell',
    layout: 'mix',
    splitMenus: true,
    collapsed:
      (initialState?.settings as { collapsed?: boolean })?.collapsed ?? false,
    onCollapse: (collapsed) =>
      setInitialState((prev: any) => ({
        ...prev,
        settings: { ...prev?.settings, collapsed },
      })),
    menu: { locale: false },
    selectedKeys: getSplitMenuSelectedKeys(history.location.pathname),
    breadcrumbRender: false,
    menuDataRender: (items) => {
      const personalAccess = getEffectiveEmployeePortalAccess(
        initialState?.authorizedResourceAccess || {},
        initialState?.employeePortalFeatures,
      );
      return filterShellMenu(items, access, history.location.pathname)
        .map((item) => {
          if (
            item.path === '/me' ||
            item.path === '/attendance-center' ||
            item.path === '/payroll-center' ||
            item.path === '/social-insurance-center' ||
            item.path === '/performance-center'
          ) {
            const routes =
              item.path === '/me'
                ? PERSONAL_ROUTES
                : item.path === '/attendance-center'
                  ? ATTENDANCE_ROUTES
                  : item.path === '/payroll-center'
                    ? PAYROLL_ROUTES
                    : item.path === '/social-insurance-center'
                      ? SOCIAL_INSURANCE_ROUTES
                      : PERFORMANCE_ROUTES;
            const children = item.children?.filter((child) => {
              const route = routes.find((entry) => entry.path === child.path);
              return !route || personalAccess[route.resource];
            });
            return children?.length ? { ...item, children } : null;
          }
          return item;
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item));
    },
    headerContentRender: () => {
      const effectiveAccess = getEffectiveEmployeePortalAccess(
        initialState?.authorizedResourceAccess || {},
        initialState?.employeePortalFeatures,
      );
      return (
        <CenterNavigation
          settingsPath={settingsPath}
          visible={{
            employee: Boolean(
              PERSONAL_ROUTES.find(({ resource }) => effectiveAccess[resource]),
            ),
            attendance: Boolean(getFirstAttendancePath(effectiveAccess)),
            payroll: Boolean(getFirstPayrollPath(effectiveAccess)),
            'social-insurance': Boolean(
              getFirstSocialInsurancePath(effectiveAccess),
            ),
            performance: Boolean(getFirstPerformancePath(effectiveAccess)),
          }}
        />
      );
    },
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <NotificationIcon key="notification" />,
        <ThemeToggle key="themeToggle" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      return showNav ? dom : false;
    },
    headerTitleRender: (logo) => (
      <HeaderTitle logo={logo} appTitle={appTitle} />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <ShellThemeBoundary scope="content">
            <div className="app-workspace">{children}</div>
          </ShellThemeBoundary>
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
        </>
      );
    },
  };
};
